import { t } from 'i18next'
import { useCallback } from 'react'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import { SimpleSwitch } from '../../../components'
import { workspaceDocumentSetStatuses } from '../../../constants/constants'
import useWorkspaceDetails from '../../../hooks/useWorkspaceDetails'
import { notificationService } from '../../../services'

const SwitchContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

const DocumentStatusSwitch = () => {
  const { workspaceId, documentId } = useParams()

  const { selectedWorkspace, updateWorkspaceDetails, workspaceDetailsLoading } =
    useWorkspaceDetails({
      workspaceId,
      preventFetch: true,
    })

  const document =
    selectedWorkspace?.config?.conformityChecksConfig?.documents?.find(
      (doc) => doc.id === documentId
    )

  const isOpened =
    document?.documentSetStatus === workspaceDocumentSetStatuses.OPENED

  const handleChange = useCallback(
    async (checked: boolean) => {
      const config = selectedWorkspace?.config
      const documents = config?.conformityChecksConfig?.documents

      if (!documents) return

      const updatedDocuments = documents.map((doc) =>
        doc.id === documentId
          ? {
              ...doc,
              documentSetStatus: checked
                ? workspaceDocumentSetStatuses.OPENED
                : workspaceDocumentSetStatuses.CLOSED,
            }
          : doc
      )

      const updatedConfig = {
        ...config,
        conformityChecksConfig: {
          ...config.conformityChecksConfig,
          documents: updatedDocuments,
        },
      }

      try {
        await updateWorkspaceDetails(updatedConfig)
        notificationService.notificationSuccess({
          messageToDisplay: t(
            `workspaceDetailsPage.documentSetStatusSwitch.${
              checked ? 'opened' : 'closed'
            }`
          ),
        })
      } catch (error) {
        console.error('Error updating document status', error)
        notificationService.notificationError({
          messageToDisplay: 'Error updating document status',
        })
      }
    },
    [documentId, selectedWorkspace, updateWorkspaceDetails]
  )

  return (
    <SwitchContainer>
      {t(
        `workspaceDetailsPage.documentStatus.${isOpened ? 'opened' : 'closed'}`
      )}
      <SimpleSwitch
        checked={isOpened}
        onClick={handleChange}
        loading={workspaceDetailsLoading}
        disabled={workspaceDetailsLoading}
      />
    </SwitchContainer>
  )
}

export default DocumentStatusSwitch
